import * as Sentry from "@sentry/nextjs";
import env from "@/lib/env";

const SENTRY_DSN = env.NEXT_PUBLIC_SENTRY_DSN;
const SENTRY_ENV = env.NODE_ENV;
const SENTRY_RELEASE = process.env.NEXT_PUBLIC_APP_VERSION ?? "0.1.0";

export function initSentry(): void {
  if (!SENTRY_DSN || SENTRY_ENV === "test") return;

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENV,
    release: SENTRY_RELEASE,
    tracesSampleRate: SENTRY_ENV === "production" ? 0.1 : 1.0,
    replaysSessionSampleRate: SENTRY_ENV === "production" ? 0.1 : 1.0,
    replaysOnErrorSampleRate: SENTRY_ENV === "production" ? 1.0 : 1.0,
    integrations: [Sentry.httpIntegration()],
    tracePropagationTargets: [
      "localhost",
      /^https:\/\/api\.hrshakti\.com/,
      env.NEXT_PUBLIC_API_URL ?? "",
    ],
    sendDefaultPii: false,
    attachStacktrace: true,
    normalizeDepth: 10,
    beforeSend(event, hint) {
      if (hint.originalException && typeof hint.originalException === "object" && "message" in hint.originalException) {
        const msg = hint.originalException.message as string;
        if (msg.includes("chunk load error")) {
          return null;
        }
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb, hint) {
      if (breadcrumb.category === "fetch" && typeof breadcrumb.message === "string" && breadcrumb.message.includes("/api/")) {
        breadcrumb.level = "info";
      }
      return breadcrumb;
    },
  });
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!SENTRY_DSN) return;
  Sentry.withScope((scope) => {
    if (context) {
      scope.setExtras(context);
    }
    Sentry.captureException(error);
  });
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = "info"): void {
  if (!SENTRY_DSN) return;
  Sentry.captureMessage(message, level);
}

export function setUserContext(user: { id?: string; email?: string; role?: string } | null): void {
  if (!SENTRY_DSN) return;
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
    Sentry.setTag("role", user.role ?? "unknown");
  } else {
    Sentry.setUser(null);
  }
}

export function addBreadcrumb(breadcrumb: Sentry.Breadcrumb): void {
  if (!SENTRY_DSN) return;
  Sentry.addBreadcrumb(breadcrumb);
}

export function startSpan(name: string, op: string = "operation"): Sentry.Span {
  if (!SENTRY_DSN) {
    return {
      end: () => undefined,
      setAttribute: () => undefined,
      setAttributes: () => undefined,
      spanContext: () => ({ traceId: "", spanId: "", sampled: false }),
      toJSON: () => ({}),
    } as unknown as Sentry.Span;
  }
  let span: Sentry.Span | undefined;
  Sentry.startSpan({ name, op }, (activeSpan) => {
    span = activeSpan;
  });
  return span ?? {
    end: () => undefined,
    setAttribute: () => undefined,
    setAttributes: () => undefined,
    spanContext: () => ({ traceId: "", spanId: "", sampled: false }),
    toJSON: () => ({}),
  } as unknown as Sentry.Span;
}

export function getActiveSpan(): Sentry.Span | null {
  const span = Sentry.getActiveSpan();
  return span ?? null;
}

export function setTag(key: string, value: string): void {
  if (!SENTRY_DSN) return;
  Sentry.setTag(key, value);
}

export function setContext(key: string, context: Record<string, unknown>): void {
  if (!SENTRY_DSN) return;
  Sentry.setContext(key, context);
}

export function flush(timeoutMs: number = 2000): Promise<boolean> {
  if (!SENTRY_DSN) return Promise.resolve(true);
  return Sentry.flush(timeoutMs);
}

export { SENTRY_DSN, SENTRY_ENV, SENTRY_RELEASE };